import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Prompt, useHistory } from 'react-router-dom'
import { message } from 'antd'
import _ from 'lodash'
import { downscaleFile, decodeImageCodes, preloadBarcodePolyfill } from '../../../shared/camera/decodeImageFile'
import { validateSewingHandover, createSewingHandover } from '../../../../services/api/3dApi'
import { getLocalData } from '../../../../services/StorageServices'
import { parseJwt } from '../../../../helpers/parseToken'
import {
    ACTIONS, ISSUE_STATUSES, ROW_STATUS, initialState, handoverReducer, getRowStatus, getNamesToCheck,
} from './handoverReducer'
import SewingHandoverPage from './SewingHandoverPage'
import '../../styles/index.scss'

const VALIDATE_DEBOUNCE_MS = 300
const VALIDATE_CHUNK = 100 // BE nhận tối đa 100 lô/lần
// SRS không giới hạn cứng (từ 50 ảnh chỉ cảnh báo); BE chặn quá 300 lô/lượt (request bất thường) → chặn sớm, tránh upload hết ảnh rồi mới bị từ chối
const MAX_ROWS = 300
const LEAVE_MESSAGE = 'Còn ảnh lệnh may chưa giao. Rời trang sẽ mất các ảnh này. Bạn chắc chắn muốn rời?'

let rowKeySeq = 0
const nextRowKey = () => `r${Date.now()}-${rowKeySeq++}`

const SewingHandoverContainer = () => {
    const [state, dispatch] = useReducer(handoverReducer, initialState)
    const history = useHistory()
    const { rows, checks, partner, submitting, result } = state

    const [partners, setPartners] = useState([])
    const [partnerTouched, setPartnerTouched] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const [cameraOpen, setCameraOpen] = useState(false)
    const [previewKey, setPreviewKey] = useState(null)
    const [lastAddedKey, setLastAddedKey] = useState(null) // ảnh vừa thêm → chip "Vừa đọc" trên camera
    const { username, isAdmin } = useMemo(() => {
        try {
            const token = parseJwt(getLocalData('accessToken'))
            return {
                username: token.username || '',
                isAdmin: (token.roles || []).some(role => role.name === 'fac_admin'),
            }
        } catch (e) {
            return { username: '', isAdmin: false }
        }
    }, [])

    const reqSeqRef = useRef(0)
    const rowsRef = useRef(rows)
    rowsRef.current = rows

    // ---- Trạng thái dẫn xuất ----
    const rowStatuses = useMemo(() => {
        const map = {}
        rows.forEach((row) => {
            map[row.key] = getRowStatus(row, rows, checks)
        })
        return map
    }, [rows, checks])

    const issueRows = rows.filter(row => ISSUE_STATUSES.includes(rowStatuses[row.key]))
    const okRows = rows.filter(row => rowStatuses[row.key] === ROW_STATUS.OK)
    const totalItems = _.sumBy(okRows, row => (checks[row.name] && checks[row.name].items_count) || 0)
    const busyRows = rows.some(row => [ROW_STATUS.DECODING, ROW_STATUS.CHECKING].includes(rowStatuses[row.key]))
    // Vị trí (1..n) của dòng vừa nhảy tới trong các dòng cần xử lý; 0 = chưa nhảy hoặc dòng đó đã hết lỗi
    const issuePosition = state.jumpKey ? issueRows.findIndex(row => row.key === state.jumpKey) + 1 : 0

    // Lý do chưa giao được; dòng lỗi đã có link "⚠ n dòng cần xử lý" ở thanh dưới
    let hint = ''
    if (!rows.length) hint = 'Chưa có ảnh lệnh may'
    else if (rows.length > MAX_ROWS) hint = `Mỗi lượt giao tối đa ${MAX_ROWS} lô, hãy xoá bớt ${rows.length - MAX_ROWS} ảnh`
    else if (busyRows) hint = 'Đang kiểm tra lô...'
    else if (!partner) hint = 'Chưa chọn đối tác may'
    const canSubmit = !hint && !issueRows.length && !submitting

    const partnerName = (partners.find(p => p._id === partner) || {}).name || ''
    const hasPending = rows.length > 0 && !result

    // ---- Màn hẹp ẩn MainMenu (CSS theo class trên body; App.js không render lại khi đổi route) ----
    useEffect(() => {
        document.body.classList.add('is-sewing-handover')
        return () => document.body.classList.remove('is-sewing-handover')
    }, [])

    // ---- Máy không có BarcodeDetector native: tải sẵn polyfill đọc nhiều QR, ảnh đầu không phải chờ ----
    useEffect(() => {
        preloadBarcodePolyfill()
    }, [])

    // ---- Dọn objectURL ----
    useEffect(() => () => {
        rowsRef.current.forEach(row => row.previewUrl && URL.revokeObjectURL(row.previewUrl))
    }, [])

    // ---- Cảnh báo rời trang (đóng tab / tải lại) ----
    useEffect(() => {
        if (!hasPending) return undefined
        const onBeforeUnload = (e) => {
            e.preventDefault()
            e.returnValue = LEAVE_MESSAGE
            return LEAVE_MESSAGE
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [hasPending])

    // ---- Kiểm tra lô với server (debounce, bỏ response cũ theo reqId) ----
    const namesToCheck = getNamesToCheck(rows, checks)
    const namesKey = namesToCheck.join('|')

    useEffect(() => {
        if (!namesKey || submitting) return undefined

        const timer = setTimeout(() => {
            _.chunk(namesKey.split('|'), VALIDATE_CHUNK).forEach(async (names) => {
                const reqId = ++reqSeqRef.current
                dispatch({ type: ACTIONS.CHECK_START, names, reqId })

                try {
                    const { data: response } = await validateSewingHandover(names)
                    const { success, data, error } = response

                    if (!success) throw new Error(error)

                    dispatch({ type: ACTIONS.CHECK_DONE, items: data.items || [], reqId })
                } catch (e) {
                    dispatch({ type: ACTIONS.CHECK_FAILED, names, reqId, message: `Không kiểm tra được lô: ${e.message}` })
                }
            })
        }, VALIDATE_DEBOUNCE_MS)

        return () => clearTimeout(timer)
    }, [namesKey, submitting])

    // ---- Thêm ảnh: thu nhỏ + nén 1 lần, dùng cho cả đọc QR và upload ----
    const _addImage = useCallback(async (rawFile) => {
        const key = nextRowKey()
        let file = rawFile

        try {
            file = await downscaleFile(rawFile, { quality: 0.8, forceJpeg: true })
        } catch (e) {
            file = rawFile
        }

        dispatch({ type: ACTIONS.ADD_ROW, key, file, previewUrl: URL.createObjectURL(file) })
        setLastAddedKey(key)

        let codes = []
        try {
            // polyfill: Safari iPhone / Chrome PC cũng đọc được ảnh dán chồng 2 QR (lệnh may)
            codes = await decodeImageCodes(file, { prepared: true, polyfill: true })
        } catch (e) {
            codes = []
        }

        dispatch({ type: ACTIONS.DECODED, key, codes })
    }, [])

    const _onPickFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter(f => f.type && f.type.indexOf('image/') === 0)
        for (const file of files) {
            // Tuần tự: đọc QR chạy trên main thread, xử lý song song nhiều ảnh lớn dễ treo máy yếu
            // eslint-disable-next-line no-await-in-loop
            await _addImage(file)
        }
    }

    // Nút "+ Nhập tay": thêm dòng không có ảnh, focus ô tên lô (FR-01 bước 5)
    const _addManual = () => {
        dispatch({ type: ACTIONS.ADD_ROW, key: nextRowKey(), file: null, previewUrl: null, manual: true })
    }

    const _removeRow = useCallback((key) => {
        const row = rowsRef.current.find(r => r.key === key)
        if (row && row.previewUrl) URL.revokeObjectURL(row.previewUrl)
        dispatch({ type: ACTIONS.REMOVE_ROW, key })
    }, [])

    // index theo danh sách ảnh của camera (chỉ các dòng có ảnh)
    const _removeShot = (index) => {
        const row = rows.filter(r => r.file)[index]
        if (row) _removeRow(row.key)
    }

    const _pickCode = useCallback((key, name) => dispatch({ type: ACTIONS.PICK_CODE, key, name }), [])
    const _setName = useCallback((key, name) => dispatch({ type: ACTIONS.SET_NAME, key, name }), [])
    const _preview = useCallback(key => setPreviewKey(key), [])

    // Nhảy lần lượt tới từng dòng cần xử lý (1/n → n/n), nổi viền và đặt con trỏ vào ô tên lô
    const _jumpToIssue = () => {
        if (!issueRows.length) return
        // Chuyển luôn sang tab Cần xử lý (BA chốt 28/10/2026); SET_FILTER đặt lại về dòng đầu
        const resetFilter = state.filter !== 'issues'
        if (resetFilter) dispatch({ type: ACTIONS.SET_FILTER, filter: 'issues' })
        if (state.search) dispatch({ type: ACTIONS.SET_SEARCH, search: '' })
        // Dòng cần xử lý kế tiếp SAU dòng vừa nhảy tới (theo thứ tự danh sách), hết thì quay về đầu.
        // Tính theo vị trí chứ không theo chỉ số: dòng vừa sửa xong rời khỏi issueRows không làm nhảy cóc dòng sau.
        const lastIndex = resetFilter || !state.jumpKey ? -1 : rows.findIndex(r => r.key === state.jumpKey)
        const row = issueRows.find(r => rows.indexOf(r) > lastIndex) || issueRows[0]
        dispatch({ type: ACTIONS.JUMP_TO, key: row.key })

        const focusRow = () => {
            const el = document.getElementById(`handover-row-${row.key}`)
            if (!el) return false
            const input = el.querySelector('.HandoverRow-name')
            if (input && !input.disabled) input.focus({ preventScroll: true })
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            el.classList.add('is-flash')
            setTimeout(() => el.classList.remove('is-flash'), 1200)
            return true
        }

        // Focus ngay trong lượt bấm để iOS chịu mở bàn phím; dòng đang bị lọc ẩn thì chờ render lại
        if (!focusRow()) setTimeout(focusRow, 50)
    }

    // ---- Giao may ----
    const _openConfirm = () => {
        setPartnerTouched(true)
        if (!partner) {
            message.error('Chưa chọn đối tác may.')
            return
        }
        if (canSubmit) setConfirming(true)
    }

    const _submit = async () => {
        if (submitting) return

        dispatch({ type: ACTIONS.SUBMIT_START })

        const formData = new FormData()
        formData.append('partner', partner)
        formData.append('batches', JSON.stringify(rows.map((row, index) => ({
            name: row.name,
            source: row.source,
            image_field: `image_${index}`,
        }))))
        rows.forEach((row, index) => {
            // Dòng nhập tay không có ảnh
            if (row.file) formData.append(`image_${index}`, row.file, `${row.name}.jpg`)
        })

        const onUploadProgress = (e) => {
            if (!e.total) return
            dispatch({ type: ACTIONS.UPLOAD_PROGRESS, percent: Math.min(100, Math.round((e.loaded / e.total) * 100)) })
        }

        try {
            const { data: response } = await createSewingHandover(formData, onUploadProgress)
            const { success, data, error, code } = response

            setConfirming(false)

            if (success) {
                rows.forEach(row => row.previewUrl && URL.revokeObjectURL(row.previewUrl))
                dispatch({ type: ACTIONS.SUBMIT_OK, handover: data.handover })
                setPartnerTouched(false)
                window.scrollTo(0, 0)
                return
            }

            if (code === 'HANDOVER_REJECTED') {
                const items = _.get(data, 'items', [])
                const invalidCount = items.filter(item => !item.valid).length
                dispatch({ type: ACTIONS.CHECKS_SET, items, atSubmit: true })
                dispatch({ type: ACTIONS.SET_FILTER, filter: 'issues' })
                dispatch({
                    type: ACTIONS.SUBMIT_FAILED,
                    message: `${invalidCount} lô không còn hợp lệ tại thời điểm giao. Xoá hoặc sửa dòng lỗi rồi bấm Giao may lại.`,
                })
                return
            }

            if (code === 'HANDOVER_LOCKED' || code === 'BATCH_CHANGED') {
                // Có thao tác khác vừa chạm vào các lô → kiểm tra lại toàn bộ
                dispatch({ type: ACTIONS.CHECKS_CLEAR })
            }

            dispatch({ type: ACTIONS.SUBMIT_FAILED, message: `${error || 'Giao may không thành công.'} Ảnh vẫn còn trên máy, bấm Giao may để thử lại.` })
        } catch (e) {
            setConfirming(false)
            const serverMessage = _.get(e, 'response.data.message') || _.get(e, 'response.data.error')
            dispatch({
                type: ACTIONS.SUBMIT_FAILED,
                message: `${serverMessage || 'Tải ảnh lên không thành công (mạng yếu hoặc mất kết nối).'} Ảnh vẫn còn trên máy, bấm Giao may để thử lại.`,
            })
        }
    }

    const _reset = () => {
        setPartnerTouched(false)
        setLastAddedKey(null)
        dispatch({ type: ACTIONS.RESET })
    }

    return (
        <>
            <Prompt when={hasPending && !submitting} message={LEAVE_MESSAGE} />
            <SewingHandoverPage
                state={state}
                rowStatuses={rowStatuses}
                issueCount={issueRows.length}
                issuePosition={issuePosition}
                okCount={okRows.length}
                totalItems={totalItems}
                canSubmit={canSubmit}
                hint={hint}
                partnerName={partnerName}
                partnerInvalid={(partnerTouched || rows.length > 0) && !partner}
                username={username}
                isAdmin={isAdmin}
                lastAddedKey={lastAddedKey}
                cameraOpen={cameraOpen}
                confirming={confirming}
                previewKey={previewKey}
                onOpenCamera={() => setCameraOpen(true)}
                onCloseCamera={() => setCameraOpen(false)}
                onCapture={_addImage}
                onRemoveShot={_removeShot}
                onPickFiles={_onPickFiles}
                onAddManual={_addManual}
                onRemoveRow={_removeRow}
                onPickCode={_pickCode}
                onSetName={_setName}
                onPreview={_preview}
                onPreviewChange={setPreviewKey}
                onJumpToIssue={_jumpToIssue}
                onRecheck={() => dispatch({ type: ACTIONS.CHECKS_CLEAR })}
                onChangeFilter={filter => dispatch({ type: ACTIONS.SET_FILTER, filter })}
                onChangeSearch={search => dispatch({ type: ACTIONS.SET_SEARCH, search })}
                onChangePartner={value => dispatch({ type: ACTIONS.SET_PARTNER, partner: value })}
                onPartnersLoaded={setPartners}
                onOpenConfirm={_openConfirm}
                onCancelConfirm={() => setConfirming(false)}
                onSubmit={_submit}
                onReset={_reset}
                onViewSlip={() => result && history.push(`/sewing-handover/${result._id}/print`)}
            />
        </>
    )
}

export default SewingHandoverContainer
